import Debug from 'debug'
import { create } from 'zustand'
import { apiClient } from '@/services/api-client'
import type { AuthResponse, JwtPayload } from '@/types/api'

const log = Debug('gg:auth')

const AUTH_TOKEN_KEY = 'auth-token-v4'

function parseJwtPayload(token: string): JwtPayload | null {
  try {
    const segments = token.split('.')
    if (segments.length !== 3) return null
    const payload = segments[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded) as JwtPayload
  } catch (err) {
    log('failed to parse JWT payload', err)
    return null
  }
}

function isTokenExpired(payload: JwtPayload): boolean {
  const nowSeconds = Math.floor(Date.now() / 1000)
  return payload.exp < nowSeconds
}

export interface AuthState {
  token: string | null
  user: JwtPayload | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  initialize: () => void
}

export const useAuthStore = create<AuthState>(set => ({
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  initialize() {
    const token = localStorage.getItem(AUTH_TOKEN_KEY)
    if (!token) {
      log('no stored token found')
      return
    }

    const user = parseJwtPayload(token)
    if (!user) {
      log('stored token is not a valid JWT - clearing')
      localStorage.removeItem(AUTH_TOKEN_KEY)
      return
    }

    if (isTokenExpired(user)) {
      log('stored token is expired - clearing')
      localStorage.removeItem(AUTH_TOKEN_KEY)
      return
    }

    log('restored session for %s', user.unique_name)
    set({ token, user, isAuthenticated: true })
  },

  async login(email: string, password: string) {
    set({ isLoading: true, error: null })

    try {
      const { data } = await apiClient.post<AuthResponse>(
        '/auth/login',
        {
          emailAddress: email,
          password,
          type: 'login',
          userAgent: navigator.userAgent,
        },
        false,
      )

      if (data.redirect) {
        log('login returned redirect: %s', data.redirect)
        window.location.href = data.redirect
        return false
      }

      const token = data.access_token
      const user = parseJwtPayload(token)
      if (!user) {
        set({ isLoading: false, error: 'Invalid token received from server' })
        return false
      }

      localStorage.setItem(AUTH_TOKEN_KEY, token)
      log('login successful for %s', user.unique_name)
      set({ token, user, isAuthenticated: true, isLoading: false, error: null })
      return true
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Login failed'
      log('login failed: %s', message)
      set({ isLoading: false, error: message })
      return false
    }
  },

  logout() {
    log('logging out')
    localStorage.removeItem(AUTH_TOKEN_KEY)
    set({ token: null, user: null, isAuthenticated: false, error: null })
  },
}))
